/**
 * WorkflowCanvas (SPEC PX-4 — workflow-builder).
 *
 * Renders a decompiled workflow node/edge graph as a visible vertical flow with
 * branch fan-out. Nodes are absolutely positioned (the `@dnd-kit/core` substrate
 * positions them; drag is enabled only in editable mode — R2). Edges are drawn as
 * custom SVG paths with labels (branch condition / split weight / event / timeout
 * / wait delay). NOT a JSON dump.
 *
 * R1 uses this read-only (no drag); R2 passes `editable` + `onSelectNode` +
 * makes the canvas a drop target.
 */

import { useDroppable } from '@dnd-kit/core';
import { GitBranch, Split, Hourglass, Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { GraphEdge, GraphNode, WorkflowGraph } from '@/types/workflow';
import { TERMINAL_NODE_ID, TRIGGER_NODE_ID } from '@/types/workflow';

const NODE_W = 220;
const NODE_H = 64;
const TERMINAL_Y_PAD = 120;

const EDGE_COLOR: Record<GraphEdge['kind'], string> = {
  linear: '#94a3b8',
  branch: '#16a34a',
  branch_default: '#f59e0b',
  split: '#8b5cf6',
  event: '#16a34a',
  timeout: '#f59e0b',
};

function nodeCenterX(node: { x: number }): number {
  return node.x + NODE_W / 2;
}

interface WorkflowCanvasProps {
  graph: WorkflowGraph;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string) => void;
  editable?: boolean;
}

export function WorkflowCanvas({
  graph,
  selectedNodeId,
  onSelectNode,
  editable = false,
}: WorkflowCanvasProps) {
  const { setNodeRef, isOver } = useDroppable({ id: 'workflow-canvas' });

  // Position lookup. The terminal marker is positioned below the last node.
  const posById = new Map<string, { x: number; y: number }>();
  graph.nodes.forEach((nd) => posById.set(nd.id, { x: nd.x, y: nd.y }));
  const maxY = graph.nodes.reduce((m, nd) => Math.max(m, nd.y), 0);
  posById.set(TERMINAL_NODE_ID, { x: 40, y: maxY + TERMINAL_Y_PAD });

  const hasTerminalEdge = graph.edges.some((e) => e.target === TERMINAL_NODE_ID);
  const canvasHeight = maxY + (hasTerminalEdge ? TERMINAL_Y_PAD + NODE_H : NODE_H) + 40;
  const canvasWidth = 520;

  return (
    <div
      ref={setNodeRef}
      data-testid="workflow-canvas"
      className={cn(
        'relative w-full h-full overflow-auto rounded-md border bg-muted/20',
        isOver && editable && 'ring-2 ring-primary'
      )}
    >
      <div
        className="relative"
        style={{ width: canvasWidth, height: canvasHeight, minHeight: '100%' }}
      >
        {/* Edges (SVG layer behind the nodes). */}
        <svg
          className="absolute inset-0 pointer-events-none"
          width={canvasWidth}
          height={canvasHeight}
          data-testid="workflow-edges"
        >
          <defs>
            {Object.entries(EDGE_COLOR).map(([kind, color]) => (
              <marker
                key={kind}
                id={`arrow-${kind}`}
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
              </marker>
            ))}
          </defs>
          {graph.edges.map((edge) => (
            <EdgePath key={edge.id} edge={edge} posById={posById} />
          ))}
        </svg>

        {/* Nodes. */}
        {graph.nodes.map((node) => (
          <NodeBox
            key={node.id}
            node={node}
            selected={selectedNodeId === node.id}
            onSelect={onSelectNode}
            editable={editable}
          />
        ))}

        {/* Terminal end-cap (only when something flows to it). */}
        {hasTerminalEdge && (
          <div
            className="absolute flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-sm"
            style={{
              left: posById.get(TERMINAL_NODE_ID)!.x,
              top: posById.get(TERMINAL_NODE_ID)!.y,
            }}
            data-testid="workflow-terminal"
          >
            <Flag className="h-3.5 w-3.5" />
            End
          </div>
        )}
      </div>
    </div>
  );
}

function EdgePath({
  edge,
  posById,
}: {
  edge: GraphEdge;
  posById: Map<string, { x: number; y: number }>;
}) {
  const src = posById.get(edge.source);
  const tgt = posById.get(edge.target);
  if (!src || !tgt) return null;

  const x1 = nodeCenterX(src);
  const y1 = src.y + NODE_H;
  const x2 = nodeCenterX(tgt);
  const y2 = tgt.y;
  const color = EDGE_COLOR[edge.kind];

  // Simple cubic curve for a flow look.
  const midY = (y1 + y2) / 2;
  const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;

  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={2}
        markerEnd={`url(#arrow-${edge.kind})`}
        strokeDasharray={edge.kind === 'branch_default' || edge.kind === 'timeout' ? '4 3' : undefined}
      />
      {edge.label && (
        <text
          x={(x1 + x2) / 2 + 6}
          y={midY - 4}
          fontSize={11}
          fill={color}
          className="select-none"
          data-testid="edge-label"
        >
          {edge.label}
        </text>
      )}
    </g>
  );
}

function nodeAccent(node: GraphNode): string {
  if (node.kind === 'trigger') return 'border-l-blue-500';
  switch (node.type) {
    case 'branch':
      return 'border-l-green-600';
    case 'wait_for_event':
      return 'border-l-amber-500';
    case 'wait':
      return 'border-l-slate-400';
    case 'send_message':
      return 'border-l-rose-500';
    case 'notify_operator':
      return 'border-l-purple-500';
    default:
      return 'border-l-primary/50';
  }
}

function nodeIcon(node: GraphNode) {
  if (node.type === 'branch') {
    const isSplit = Array.isArray(node.config.split_branches) && (node.config.split_branches as unknown[]).length > 0;
    return isSplit ? <Split className="h-4 w-4" /> : <GitBranch className="h-4 w-4" />;
  }
  if (node.type === 'wait_for_event') return <Hourglass className="h-4 w-4" />;
  return null;
}

function nodeSubtitle(node: GraphNode): string | null {
  const c = node.config;
  if (node.kind === 'trigger') {
    const t = c.tag || c.to_stage || c.field_key || c.channel;
    return t ? String(t) : null;
  }
  switch (node.type) {
    case 'wait':
      return `${(c.delay_minutes as number) ?? 0} min delay`;
    case 'send_message':
      return c.channel ? `via ${c.channel}` : c.template_id ? `template ${c.template_id}` : 'message';
    case 'notify_operator':
      return c.message ? String(c.message).slice(0, 40) : 'notify';
    case 'update_field':
      return c.field_path ? `${c.collection ?? 'contacts'}.${c.field_path}` : null;
    case 'enroll_sequence':
      return c.sequence_definition_id ? String(c.sequence_definition_id) : null;
    case 'wait_for_event':
      return c.event_type ? `event: ${c.event_type}` : null;
    default:
      return null;
  }
}

function NodeBox({
  node,
  selected,
  onSelect,
  editable,
}: {
  node: GraphNode;
  selected: boolean;
  onSelect?: (id: string) => void;
  editable: boolean;
}) {
  const subtitle = nodeSubtitle(node);
  return (
    <div
      data-testid={node.id === TRIGGER_NODE_ID ? 'node-trigger' : `node-${node.id}`}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={() => onSelect?.(node.id)}
      onKeyDown={(e) => {
        if (onSelect && (e.key === 'Enter' || e.key === ' ')) onSelect(node.id);
      }}
      className={cn(
        'absolute rounded-md border bg-card shadow-sm border-l-4 px-3 py-2',
        nodeAccent(node),
        onSelect && 'cursor-pointer hover:shadow-md',
        editable && onSelect && 'cursor-pointer',
        selected && 'ring-2 ring-primary'
      )}
      style={{ left: node.x, top: node.y, width: NODE_W, minHeight: NODE_H }}
    >
      <div className="flex items-center gap-2">
        {node.kind === 'trigger' ? (
          <Badge className="bg-blue-100 text-blue-800">Trigger</Badge>
        ) : (
          nodeIcon(node)
        )}
        <span className="text-sm font-medium truncate">{node.type}</span>
      </div>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
      )}
    </div>
  );
}
